import React, { useState, useEffect } from 'react';
import { Link } from 'react-router';
import * as yup from 'yup';
import axios from 'axios';
import { Formik, FormikProps } from 'formik';
import LabelledInput from '@ordermentum/ui-shared/molecules/LabelledInput';
import { Button } from '@ordermentum/ui-shared/atoms/Button';
import { safeRedirect } from '@ordermentum/ui-shared/helpers/urls';
import Banner from '@ordermentum/ui-shared/atoms/Banner';
import { getUserContext } from '@ordermentum/ui-shared/utilities/user';
import * as LocalStorageUtility from '@ordermentum/ui-shared/utilities/localStorage';
import { SignInContainer } from '../LoginBase.styles';
import {
  AuthActionContainer,
  ErrorBannerWrapper,
  FieldWrapper,
  FormWrapper,
  SignInLabelContainer,
  SignInSwitch,
  SignInText,
} from './index.styles';
import { SubmissionButton } from './LoginForm';
import { MessageBanner } from './MessageBanner';
import Policies from '../Policies';
import { SignUpFormProps, SignUpFormValues } from './index.types';
import history from '../../../lib/history';

export const SignUpForm = (props: SignUpFormProps) => {
  const {
    mode,
    inviteToken,
    inviteId,
    registrationSuccessDestination,
    onSignInClick,
    messageBannerState,
    campaign,
    recipientEmail,
    paymentRequestId,
  } = props;
  const [loginBannerState, setLoginBannerState] =
    useState<any>(messageBannerState);
  useEffect(() => {
    window.analytics?.track('signup', getUserContext());
  }, []);

  const [reqError, setReqError] = useState<string | undefined>();

  const SignInLink = ({ children }: React.PropsWithChildren<{}>) =>
    onSignInClick ? (
      <Button type="reset" onClick={onSignInClick}>
        {children}
      </Button>
    ) : (
      <Link to="/">{children}</Link>
    );

  const LoginForm = ({ formik }: { formik: FormikProps<SignUpFormValues> }) => (
    <form onSubmit={formik.handleSubmit}>
      <FormWrapper>
        <FieldWrapper>
          <LabelledInput
            label="Email address"
            required
            tag="email"
            value={formik.values.email}
            onChange={formik.setFieldValue}
            size="max"
          />
        </FieldWrapper>
        <FieldWrapper>
          <LabelledInput
            label="First name"
            required
            tag="firstName"
            value={formik.values.firstName}
            onChange={formik.setFieldValue}
            size="max"
          />
        </FieldWrapper>
        <FieldWrapper>
          <LabelledInput
            label="Last name"
            tag="lastName"
            value={formik.values.lastName}
            onChange={formik.setFieldValue}
            size="max"
          />
        </FieldWrapper>
        <FieldWrapper>
          <LabelledInput
            label="Phone number"
            tag="phone"
            value={formik.values.phone}
            onChange={formik.setFieldValue}
            size="max"
          />
        </FieldWrapper>
        <FieldWrapper>
          <LabelledInput
            label="Create password"
            tag="password"
            required
            value={formik.values.password}
            onChange={formik.setFieldValue}
            size="max"
            type="password"
          />
        </FieldWrapper>
        <AuthActionContainer data-testid="create-account-submit-button">
          <SubmissionButton loading={formik.isSubmitting}>
            Create account
          </SubmissionButton>
        </AuthActionContainer>
        <Policies />
      </FormWrapper>
    </form>
  );

  const inviteSignupSubmit = async values =>
    axios.post(`/v1/users/invite-signup`, {
      token: inviteToken,
      inviteId,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      destination: registrationSuccessDestination,
      password: values.password,
    });

  const submit = async values => {
    setReqError(undefined);
    if (mode === 'invitation' && recipientEmail === values.email) {
      try {
        const result = await inviteSignupSubmit(values);
        if (result.data.destination) {
          window.location.href = safeRedirect(result.data.destination);
          return;
        }
        history.push('/sign-up/success');
      } catch (ex) {
        setLoginBannerState('error');
      }
    } else {
      try {
        await axios.post('/v1/register', {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          phone: values.phone,
          destination: registrationSuccessDestination,
          campaign,
          password: values.password,
        });
        history.push('/sign-up/success');
      } catch (err) {
        setReqError(err.response.data.message);
      }
    }
  };

  const getInitialValues = () => {
    const defaultValues = {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
    };
    try {
      const storageItem = LocalStorageUtility.getItem(
        `dd-$${paymentRequestId}`
      );
      const userData = JSON.parse(storageItem ?? '{}');
      if (userData?.name) {
        const [firstName, ...lastName] = userData.name.split(' ');
        LocalStorageUtility.deleteItem(`dd-$${paymentRequestId}`);
        return {
          email: userData.email,
          phone: userData.phone,
          firstName,
          lastName: lastName.join(' '),
        };
      }
      return defaultValues;
    } catch {
      return defaultValues;
    }
  };

  return (
    <SignInContainer>
      {loginBannerState && <MessageBanner type={loginBannerState} />}
      <SignInLabelContainer>
        <SignInText>Create an account</SignInText>
        <SignInSwitch data-testid="signin-link">
          or <SignInLink>sign in</SignInLink>
        </SignInSwitch>
      </SignInLabelContainer>
      {reqError && (
        <ErrorBannerWrapper>
          <Banner type="warning" noIcon dismiss={false}>
            {reqError}
          </Banner>
        </ErrorBannerWrapper>
      )}
      <Formik
        initialValues={getInitialValues()}
        enableReinitialize
        validationSchema={() =>
          yup.object().shape({
            email: yup.string().required(),
            firstName: yup.string().required(),
            lastName: yup.string(),
            phone: yup.string(),
            password: yup.string().required(),
          })
        }
        onSubmit={submit}
      >
        {formik => <LoginForm formik={formik} />}
      </Formik>
    </SignInContainer>
  );
};
